import { useEffect, useState } from 'react';
import { LuInfo } from 'react-icons/lu';
import { Badge, Button } from '@qodo/design-system';
import { QUERY_PARAMS, QUERY_PARAM_VALUES } from 'routes/consts';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { LoginMethod } from 'services/mixpanel/types';
import { Alert, AlertDescription } from 'ui/alert';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { getQueryParam, storeMarketingParamsInLocalStorage } from 'utils/url';
import { ProviderConfig } from '../types';
import { useProvidersConfiguration } from '../useProvidersConfiguration';
import { Title } from './Title';

export const SigninMethods = () => {
  const spawnText = spawn('login.signin-methods');
  const { providers } = useProvidersConfiguration();

  const [loadingProvider, setLoadingProvider] = useState<
    ProviderConfig['provider'] | undefined
  >(undefined);

  const githubProvider = providers.find((p) => p.provider === 'github');
  const nonGithubProviders = providers.filter((p) => p.provider !== 'github');

  const isIdpInitiated =
    getQueryParam(QUERY_PARAMS.SOURCE) ===
    QUERY_PARAM_VALUES.SOURCE.IDP_INITIATED;

  const onProviderClick = async (
    provider: ProviderConfig['provider'],
    onClick: () => void,
  ) => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.SELECT_SIGNUP_METHOD,
      interaction_details: {
        login_method: provider as LoginMethod,
      },
    });

    setLoadingProvider(provider);

    try {
      await onClick();
    } finally {
      setLoadingProvider(undefined);
    }
  };

  useEffect(() => {
    storeMarketingParamsInLocalStorage();
  }, []);

  const getProviderText = (provider: string) =>
    spawnText(`provider.${provider}`);

  const renderButton = ({
    icon,
    provider,
    onClick = () => {},
    variant = 'outline',
    className,
  }: ProviderConfig) => {
    const isCurrentProviderLoading = loadingProvider === provider;
    const isAnyProviderLoading = loadingProvider !== undefined;

    return (
      <Button
        key={provider}
        className={cn('w-full gap-4!', className)}
        variant={variant}
        loading={isCurrentProviderLoading}
        disabled={isAnyProviderLoading}
        iconNode={icon}
        onClick={() => onProviderClick(provider, onClick)}
      >
        <span className="hidden sm:block text-center text-xs uppercase font-medium tracking-[0.075rem]">
          {getProviderText(provider)}
        </span>
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-8 font-mono items-center max-w-65">
      {isIdpInitiated && (
        <Alert variant="info" className="flex items-start gap-2" role="status">
          <LuInfo className="size-4 mt-0.5 shrink-0" aria-hidden="true" />
          <AlertDescription>
            {spawnText('idp-initiated-message')}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <Title className="font-figtree text-purple-500 text-center">
          {spawnText('title')}
        </Title>
        <span className="text-[18px] font-normal text-center text-text-general-neutral-subtle font-figtree leading-normal">
          {spawnText('subtitle')}
        </span>
      </div>

      <div className="flex flex-col gap-4 w-full">
        {githubProvider && renderButton(githubProvider)}
        {nonGithubProviders.map(renderButton)}
      </div>

      <Badge size="lg" variant="emphasis" className="font-inter bg-transparent">
        {spawnText('credit-card-message')}
      </Badge>
    </div>
  );
};
